import React, { useState } from "react";
import {
  Card,
  Input,
  Button,
  Spin,
  Alert,
  Typography,
  Descriptions,
  Collapse,
  message,
  Tag,
  Space,
  Tooltip,
} from "antd";
import { useQuery, useMutation } from "react-query";
import { useSelector } from "react-redux";
import { Locale } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import newRequest, { buildBaseURL } from "../../utils/http/newRuequest";
import Base from "../Base";

const { Paragraph } = Typography;

const i18n = Locale.getInstance();

const ORDER_DEBUG_API = { name: "order debug api" };

const AMOUNT_KEYS = [
  "price",
  "ship_price",
  "total",
  "total_fee",
  "total_amount",
  "profitUnfinishedAmount",
  "total_price",
  "refund_price",
  "finish_amount",
  "commission",
  "original_price",
  "amount",
  "product_price",
  "product_price_payment_fee",
  "ship_price_payment_fee",
  "packing_price",
  "packing_price_payment_fee",
  "payment_fee",
  "platform_income",
  "platform_expense",
  "merchant_income",
  "merchant_expense",
  "bb_agent_expense",
  "bb_agent_income",
  "orderTotal",
  "maxSharable",
  "safeMaxCommission",
  "maxAgentCommission",
  "paymentFee",
  "specialShare",
  "agentCommission",
  "totalPlannedShare",
  "remainingForMerchant",
  "estimatedAmount",
  "uncheckoutCommissionTotal",
];
const DATE_KEYS = ["createdAt", "success_time", "order_date", "created_at", "updated_at", "received_at", "delivering_at", "deliveringAt", "settlementAt", "profitSharingLockExpiresAt", "uploadedAt"];

/** Turn keys like "orderId" or "order_no" into fallback label */
function toLabel(key) {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^\s+/, "")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Human-friendly value for display (amounts in yuan, dates formatted, booleans localized) */
function formatValue(val, key) {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean")
    return val ? i18n.t("common.yes") : i18n.t("common.no");
  if (typeof val === "object") return JSON.stringify(val);
  if (
    typeof val === "number" &&
    AMOUNT_KEYS.includes(key)
  ) {
    return `${(val / 100).toFixed(2)} ${i18n.t("common.yuan")}`;
  }
  if (DATE_KEYS.includes(key) && typeof val === "string") {
    try {
      const d = new Date(val);
      if (!Number.isNaN(d.getTime()))
        return d.toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        });
    } catch (_) {}
  }
  return String(val);
}

/** Get localized field label */
function getFieldLabel(key) {
  const tKey = `admin.orderDebug.fields.${key}`;
  const t = i18n.t(tKey);
  return t !== tKey ? t : toLabel(key);
}

/** Get localized section title */
function getSectionTitle(key) {
  const tKey = `admin.orderDebug.sections.${key}`;
  const t = i18n.t(tKey);
  return t !== tKey ? t : toLabel(key);
}

/** One object as label-value rows in a table-like block; excludeKeys are omitted from the table. 3 columns per row. */
function KeyValueTable({
  data,
  bordered = true,
  size = "small",
  column = 3,
  excludeKeys = [],
}) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const entries = Object.entries(data).filter(
    ([key]) => !excludeKeys.includes(key)
  );
  if (entries.length === 0) {
    return (
      <pre style={{ margin: 0, fontSize: 12, color: "#666" }}>
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  }
  return (
    <Descriptions
      bordered={bordered}
      size={size}
      column={column}
    >
      {entries.map(([key, value]) => (
        <Descriptions.Item key={key} label={getFieldLabel(key)}>
          {formatValue(value, key)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
}

function SharingPlanSection({ plan, titleSuffix = "" }) {
  if (!plan || typeof plan !== "object") return null;
  const title = `${getSectionTitle("sharingPlan")}${titleSuffix}`;
  return (
    <Card
      size="small"
      title={title}
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: "16px 24px" }}
    >
      <KeyValueTable
        data={{
          v2Enabled: plan.v2Enabled,
          lastRoundType: plan.lastRoundType,
          hasAgentCommission: plan.hasAgentCommission,
          hasSpecialShare: plan.hasSpecialShare,
        }}
      />
      {plan.budget && (
        <Card type="inner" size="small" title={getFieldLabel("budget")} style={{ marginTop: 12 }}>
          <KeyValueTable data={plan.budget} />
        </Card>
      )}
      {Array.isArray(plan.rounds) && plan.rounds.length > 0 && (
        <Card type="inner" size="small" title={getFieldLabel("rounds")} style={{ marginTop: 12 }}>
          {plan.rounds.map((round, idx) => (
            <div key={round.type || idx} style={{ marginBottom: idx < plan.rounds.length - 1 ? 12 : 0 }}>
              <Typography.Text strong>
                #{idx + 1} {getFieldLabel(round.type || "type")}
              </Typography.Text>
              <KeyValueTable data={round} />
            </div>
          ))}
        </Card>
      )}
      {plan.legacyComparison && (
        <Card type="inner" size="small" title={getFieldLabel("legacyComparison")} style={{ marginTop: 12 }}>
          <KeyValueTable data={plan.legacyComparison} />
        </Card>
      )}
    </Card>
  );
}

const CHECK_SEVERITY_COLORS = {
  blocker: "red",
  warning: "orange",
  info: "blue",
  ok: "green",
};

function getCheckLabel(code) {
  const tKey = `admin.orderDebug.agentSharingChecks.${code}`;
  const t = i18n.t(tKey);
  return t !== tKey ? t : code;
}

function AgentSharingDiagnosticsSection({ diagnostics }) {
  if (!diagnostics || typeof diagnostics !== "object") return null;

  const sharingNotRequired = diagnostics.wechatAgentSharingRequired === false;
  const alertType = sharingNotRequired
    ? "info"
    : diagnostics.canCreateWithdrawal
      ? diagnostics.warningCount > 0
        ? "warning"
        : "success"
      : "error";

  return (
    <Card
      size="small"
      title={getSectionTitle("agentSharingDiagnostics")}
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: "16px 24px" }}
    >
      <Alert
        type={alertType}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          sharingNotRequired
            ? i18n.t("admin.orderDebug.agentSharingNotRequired")
            : diagnostics.canCreateWithdrawal
              ? i18n.t("admin.orderDebug.agentSharingEligible")
              : i18n.t("admin.orderDebug.agentSharingBlocked")
        }
        description={diagnostics.summary}
      />

      {diagnostics.recommendedCommand && (
        <Paragraph copyable={{ text: diagnostics.recommendedCommand }} style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">{i18n.t("admin.orderDebug.recommendedCommand")}: </Typography.Text>
          <Typography.Text code>{diagnostics.recommendedCommand}</Typography.Text>
        </Paragraph>
      )}

      {(diagnostics.uncheckoutCommissionTotal !== undefined ||
        diagnostics.maxAgentCommission !== undefined) && (
        <KeyValueTable
          data={{
            uncheckoutCommissionTotal: diagnostics.uncheckoutCommissionTotal,
            maxAgentCommission: diagnostics.maxAgentCommission,
            agentRoundBlocker: diagnostics.agentRoundBlocker || "—",
          }}
        />
      )}

      {Array.isArray(diagnostics.finishBlockers) && diagnostics.finishBlockers.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12, marginBottom: 12 }}
          message={i18n.t("admin.orderDebug.finishBlockers")}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {diagnostics.finishBlockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          }
        />
      )}

      {diagnostics.settings && (
        <Card type="inner" size="small" title={getFieldLabel("merchantSettings")} style={{ marginTop: 12, marginBottom: 12 }}>
          <KeyValueTable data={diagnostics.settings} />
        </Card>
      )}

      {Array.isArray(diagnostics.checks) && diagnostics.checks.length > 0 && (
        <Card type="inner" size="small" title={i18n.t("admin.orderDebug.checklist")}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {diagnostics.checks.map((check) => (
              <div key={check.code}>
                <Space wrap>
                  <Tag color={check.passed ? "green" : CHECK_SEVERITY_COLORS[check.severity] || "default"}>
                    {check.passed ? i18n.t("admin.orderDebug.checkPassed") : i18n.t("admin.orderDebug.checkFailed")}
                  </Tag>
                  <Typography.Text strong>{getCheckLabel(check.code)}</Typography.Text>
                  <Tag>{check.severity}</Tag>
                </Space>
                <div style={{ marginTop: 4 }}>
                  <Tooltip title={check.code}>
                    <Typography.Text type="secondary">{check.message}</Typography.Text>
                  </Tooltip>
                </div>
                {check.detail && (
                  <pre style={{ margin: "8px 0 0", fontSize: 11, background: "#fafafa", padding: 8, borderRadius: 4 }}>
                    {JSON.stringify(check.detail, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </Space>
        </Card>
      )}
    </Card>
  );
}

function WechatSettlementSection({ settlement }) {
  if (!settlement || typeof settlement !== "object") return null;

  const alertType = settlement.awaitingWechatSettlement
    ? "warning"
    : settlement.settlementReady
      ? settlement.pendingSharingRows?.length > 0
        ? "warning"
        : "success"
      : "info";

  const summaryTable = {
    uploadDeliveryEnabled: settlement.uploadDeliveryEnabled,
    deliveringAt: settlement.deliveringAt,
    settlementReady: settlement.settlementReady,
    minutesUntilSettlementReady: settlement.minutesUntilSettlementReady,
    settlementAt: settlement.settlementAt,
    awaitingWechatSettlement: settlement.awaitingWechatSettlement,
    profitSharingRequestLocked: settlement.profitSharingRequestLocked,
    profitSharingLockExpiresAt: settlement.profitSharingLockExpiresAt,
  };

  return (
    <Card
      size="small"
      title={getSectionTitle("wechatSettlement")}
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: "16px 24px" }}
    >
      <Alert
        type={alertType}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          settlement.awaitingWechatSettlement
            ? i18n.t("admin.orderDebug.wechatSettlementWaiting")
            : settlement.settlementReady
              ? i18n.t("admin.orderDebug.wechatSettlementReady")
              : i18n.t("admin.orderDebug.wechatSettlementStatus")
        }
        description={settlement.summary}
      />

      <KeyValueTable data={summaryTable} />

      {Array.isArray(settlement.sharingDeferralReasons) &&
        settlement.sharingDeferralReasons.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={i18n.t("admin.orderDebug.sharingDeferralReasons")}
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {settlement.sharingDeferralReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            }
          />
        )}

      {Array.isArray(settlement.pendingSharingRows) &&
        settlement.pendingSharingRows.length > 0 && (
          <Card
            type="inner"
            size="small"
            title={getFieldLabel("pendingSharingRows")}
            style={{ marginTop: 12 }}
          >
            {settlement.pendingSharingRows.map((row, idx) => (
              <div
                key={row.id || idx}
                style={{
                  marginBottom:
                    idx < settlement.pendingSharingRows.length - 1 ? 12 : 0,
                }}
              >
                <Typography.Text strong>#{idx + 1}</Typography.Text>
                <KeyValueTable data={row} />
              </div>
            ))}
          </Card>
        )}

      {Array.isArray(settlement.deliveryUploads) &&
        settlement.deliveryUploads.length > 0 && (
          <Card
            type="inner"
            size="small"
            title={`${getFieldLabel("deliveryUploads")} · ${i18n.t("admin.orderDebug.itemCount", { count: settlement.deliveryUploads.length })}`}
            style={{ marginTop: 12 }}
          >
            <Collapse defaultActiveKey={["upload-0"]} expandIconPosition="end" ghost>
              {settlement.deliveryUploads.map((upload, idx) => (
                <Collapse.Panel
                  key={`upload-${idx}`}
                  header={
                    <Space wrap>
                      <span>#{idx + 1}</span>
                      <Tag color={upload.shippingSuccess ? "green" : "red"}>
                        {getFieldLabel("shippingSuccess")}
                      </Tag>
                      <Tag color={upload.confirmReceiveSuccess ? "green" : "red"}>
                        {getFieldLabel("confirmReceiveSuccess")}
                      </Tag>
                      {upload.context && <Tag>{upload.context}</Tag>}
                    </Space>
                  }
                >
                  <KeyValueTable data={upload} />
                </Collapse.Panel>
              ))}
            </Collapse>
          </Card>
        )}
    </Card>
  );
}

function OrderDebugResult({ data }) {
  if (!data) return null;

  const sections = [];
  const cardBodyStyle = { padding: "16px 24px" };

  if (data.order) {
    sections.push(
      <Card
        key="order"
        size="small"
        title={getSectionTitle("order")}
        style={{ marginBottom: 16 }}
        bodyStyle={cardBodyStyle}
      >
        <KeyValueTable data={data.order} />
      </Card>
    );
  }
  if (data.wechatPay != null) {
    sections.push(
      <Card
        key="wechatPay"
        size="small"
        title={getSectionTitle("wechatPay")}
        style={{ marginBottom: 16 }}
        bodyStyle={cardBodyStyle}
      >
        <KeyValueTable data={data.wechatPay} />
      </Card>
    );
  }
  if (data.sharingPlan != null) {
    if (Array.isArray(data.sharingPlan)) {
      data.sharingPlan.forEach((plan, idx) => {
        sections.push(
          <SharingPlanSection
            key={`sharingPlan-${idx}`}
            plan={plan}
            titleSuffix={` · #${idx + 1}`}
          />
        );
      });
    } else {
      sections.push(<SharingPlanSection key="sharingPlan" plan={data.sharingPlan} />);
    }
  }
  if (data.agentSharingDiagnostics) {
    sections.push(
      <AgentSharingDiagnosticsSection
        key="agentSharingDiagnostics"
        diagnostics={data.agentSharingDiagnostics}
      />
    );
  }
  if (data.wechatSettlement) {
    sections.push(
      <WechatSettlementSection
        key="wechatSettlement"
        settlement={data.wechatSettlement}
      />
    );
  }
  const arrayKeys = [
    "combineOrderDetails",
    "refunds",
    "combineOrderRefundDetails",
    "wechatProfitSharing",
    "wechatProfitSharingFinish",
    "bulutBazarAgentCheckoutRecords",
    "bulutBazarMerchantCheckoutRecords",
    "vipCommissions",
    "commissionWithdrawals",
  ];
  const nestedConfig = {
    vipCommissions: { excludeKeys: ["commissionDetails"], nestedKey: "commissionDetails" },
    wechatProfitSharing: { excludeKeys: ["details"], nestedKey: "details" },
  };

  arrayKeys.forEach((key) => {
    const arr = data[key];
    if (!Array.isArray(arr) || arr.length === 0) return;
    const title = `${getSectionTitle(key)} · ${i18n.t("admin.orderDebug.itemCount", { count: arr.length })}`;
    const config = nestedConfig[key] || { excludeKeys: [], nestedKey: null };
    const itemKeys = arr.map((_, idx) => `item-${idx}`);
    sections.push(
      <Card
        key={key}
        size="small"
        title={title}
        style={{ marginBottom: 16 }}
        bodyStyle={cardBodyStyle}
      >
        <Collapse
          defaultActiveKey={itemKeys}
          expandIconPosition="end"
          ghost
        >
          {arr.map((item, idx) => (
            <Collapse.Panel key={`item-${idx}`} header={`#${idx + 1}`}>
              <div style={{ paddingTop: 8 }}>
                <KeyValueTable data={item} excludeKeys={config.excludeKeys} />
                {config.nestedKey &&
                  item[config.nestedKey] &&
                  item[config.nestedKey].length > 0 && (
                    <Card
                      type="inner"
                      size="small"
                      title={getFieldLabel(config.nestedKey)}
                      style={{ marginTop: 12 }}
                    >
                      {item[config.nestedKey].map((detail, i) => (
                        <div key={i} style={{ marginBottom: i < item[config.nestedKey].length - 1 ? 12 : 0 }}>
                          <Typography.Text strong>#{i + 1}</Typography.Text>
                          <KeyValueTable data={detail} />
                        </div>
                      ))}
                    </Card>
                  )}
              </div>
            </Collapse.Panel>
          ))}
        </Collapse>
      </Card>
    );
  });
  if (
    data.wechatProfitSharingFinish != null &&
    !Array.isArray(data.wechatProfitSharingFinish)
  ) {
    sections.push(
      <Card
        key="wechatProfitSharingFinish"
        size="small"
        title={getSectionTitle("wechatProfitSharingFinish")}
        style={{ marginBottom: 16 }}
        bodyStyle={cardBodyStyle}
      >
        <KeyValueTable data={data.wechatProfitSharingFinish} />
      </Card>
    );
  }
  return (
    <div style={{ marginTop: 16 }}>
      {sections}
      <Collapse
        size="small"
        items={[
          {
            key: "raw",
            label: i18n.t("admin.orderDebug.rawJson"),
            children: (
              <pre
                style={{
                  background: "#f5f5f5",
                  padding: 12,
                  borderRadius: 4,
                  overflow: "auto",
                  maxHeight: 400,
                  margin: 0,
                }}
              >
                {JSON.stringify(data, null, 2)}
              </pre>
            ),
          },
        ]}
      />
    </div>
  );
}

async function postOrderDebugSync(orderId, token) {
  const baseUrl = buildBaseURL("v6", "");
  const url = `${baseUrl}merchant/order/${orderId}/debug-sync`;
  const res = await newRequest.post(url, {}, token);
  if (!res?.result || res.data == null) {
    throw new Error(res?.message || i18n.t("admin.orderDebug.syncFailed"));
  }
  return res.data;
}

async function fetchOrderDebug(orderId, token) {
  const baseUrl = buildBaseURL("v6", "");
  const url = `${baseUrl}merchant/order/${orderId}/debug`;
  try {
    const res = await newRequest.get(url, token);
    if (!res?.result || res.data == null) {
      const err = new Error(res?.message || i18n.t("admin.orderDebug.fetchFailed"));
      err.response = res;
      throw err;
    }
    return res.data;
  } catch (err) {
    if (err?.response) throw err;
    throw Object.assign(new Error(err?.message || i18n.t("admin.orderDebug.fetchFailed")), {
      response: err,
      status: err?.status,
    });
  }
}

const OrderDebug = () => {
  const user = useSelector((state) => getUserInfo(state));
  const initialOrderId = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("orderId") || "";
    } catch (_) {
      return "";
    }
  })();
  const [orderId, setOrderId] = useState(initialOrderId);
  const [searchId, setSearchId] = useState(() => {
    const n = parseInt(initialOrderId, 10);
    return !Number.isNaN(n) && n > 0 ? n : null;
  });

  const base = Base.getInstance();

  const {
    data,
    error,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["orderDebug", searchId],
    queryFn: () => fetchOrderDebug(searchId, user?.token),
    enabled: searchId != null && searchId > 0,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (err) => {
      base.handleApiError(ORDER_DEBUG_API, err);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => postOrderDebugSync(searchId, user?.token),
    onSuccess: () => {
      message.success(i18n.t("admin.orderDebug.syncSuccess"));
      refetch();
    },
    onError: (err) => {
      message.error(err?.message || i18n.t("admin.orderDebug.syncFailed"));
      base.handleApiError(ORDER_DEBUG_API, err);
    },
  });

  const canSync =
    searchId != null &&
    searchId > 0 &&
    data?.order?.isProfitSharingOrder;

  const handleFetch = () => {
    const id = orderId?.trim();
    if (!id) {
      message.warning(i18n.t("admin.orderDebug.enterOrderId"));
      return;
    }
    const numId = parseInt(id, 10);
    if (Number.isNaN(numId) || numId < 1) {
      message.warning(i18n.t("admin.orderDebug.invalidOrderId"));
      return;
    }
    setSearchId(numId);
  };

  const errorMessage =
    isError && error?.message
      ? error.message
      : isError
        ? i18n.t("admin.orderDebug.fetchFailed")
        : null;
  const loading = isLoading || isFetching;

  return (
    <div style={{ padding: 24 }}>
      <Card title={i18n.t("admin.orderDebug.title")}>
        <Paragraph type="secondary">
          {i18n.t("admin.orderDebug.description")}
        </Paragraph>
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 16,
            maxWidth: 400,
          }}
        >
          <Input
            placeholder={i18n.t("admin.orderDebug.orderIdPlaceholder")}
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            onPressEnter={handleFetch}
            disabled={loading}
          />
          <Button type="primary" onClick={handleFetch} loading={loading}>
            {i18n.t("common.search")}
          </Button>
          {canSync && (
            <Button
              onClick={() => syncMutation.mutate()}
              loading={syncMutation.isLoading}
            >
              {i18n.t("admin.orderDebug.syncWeChat")}
            </Button>
          )}
        </div>
        {errorMessage && (
          <Alert
            message={i18n.t("admin.orderDebug.error")}
            description={errorMessage}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {loading && <Spin />}
        {data != null && !loading && <OrderDebugResult data={data} />}
      </Card>
    </div>
  );
};

export default OrderDebug;
